import _jsx from "@babel/runtime/helpers/jsx";
import React from 'react';
import PropTypes from 'prop-types';
import defaultConfig from '../config';
import JWPlayer from '../JWPlayer';
import SisterPlayer from '../SisterPlayer';
import LiquidusPlayer from '../LiquidusPlayer';

/**
 *  The Video component renders a either Sister, Liquidus, or the default JWPlayer for all videos based on src.
 *  They all expose onPlayerReady, onPlay, onReplay and onCompleted callbacks to the parent component.
 *  Depending on the player, certain props may be required, but all videos must have a width, height, src and videoId.
*/
function Video(_ref) {
  var _JWPlayer, _SisterPlayer, _LiquidusPlayer;
  let {
    active,
    autostart = false,
    className,
    config = defaultConfig,
    controlbar,
    dock,
    height,
    image,
    lightbox,
    mute = false,
    onPlayerReady,
    onPlay,
    onReplay,
    onCompleted,
    onMilestones,
    playlist,
    provider,
    repeat = false,
    smoothing,
    src,
    stretching,
    videoId,
    width,
    tracks,
    placeholder
  } = _ref;
  const getVendor = () => {
    let vendor = '';
    if (src.match(/sister\.tv/i) !== null) {
      vendor = 'sister';
    } else if (src.match(/liquidus\.net/i) !== null) {
      vendor = 'aqueousdt';
    } else if (src.match(/dmotorworks\.com/i) !== null) {
      // Ford CPO's video URL
      vendor = 'aqueousdt';
    } else if (src.match(/aqueousdt\.com/i) !== null) {
      vendor = 'aqueousdt';
    }
    return vendor;
  };
  const videoPlaceholder = placeholder ? /*#__PURE__*/React.cloneElement(placeholder, {
    width,
    height
  }) : null;
  const renderJWPlayer = () => _JWPlayer || (_JWPlayer = /*#__PURE__*/_jsx(JWPlayer, {
    className: className,
    videoId: videoId,
    src: src,
    width: width,
    height: height,
    image: image,
    provider: provider,
    repeat: repeat,
    mute: mute,
    dock: dock,
    smoothing: smoothing,
    stretching: stretching,
    controlbar: controlbar,
    playlist: playlist,
    config: config,
    onPlayerReady: onPlayerReady,
    onPlay: onPlay,
    onReplay: onReplay,
    onCompleted: onCompleted,
    onMilestones: onMilestones,
    tracks: tracks,
    placeholder: videoPlaceholder
  }));
  const renderSisterPlayer = () => _SisterPlayer || (_SisterPlayer = /*#__PURE__*/_jsx(SisterPlayer, {
    width: width,
    height: height,
    className: className,
    videoId: videoId,
    src: src,
    image: image,
    config: config,
    active: active,
    lightbox: lightbox,
    onPlayerReady: onPlayerReady,
    onPlay: onPlay,
    onReplay: onReplay,
    onCompleted: onCompleted,
    placeholder: videoPlaceholder
  }));
  const renderLiquidusPlayer = () => _LiquidusPlayer || (_LiquidusPlayer = /*#__PURE__*/_jsx(LiquidusPlayer, {
    className: className,
    videoId: videoId,
    width: width,
    height: height,
    autostart: autostart,
    config: config,
    lightbox: lightbox,
    onPlayerReady: onPlayerReady,
    onPlay: onPlay,
    onReplay: onReplay,
    onCompleted: onCompleted,
    src: src,
    placeholder: videoPlaceholder
  }));
  const vendor = getVendor();
  let renderer;
  switch (vendor) {
    case 'sister':
      renderer = renderSisterPlayer;
      break;
    case 'aqueousdt':
      renderer = renderLiquidusPlayer;
      break;
    default:
      renderer = renderJWPlayer;
  }
  return renderer();
}
Video.propTypes = {
  /**
   * Video Id is to be passed for video ref instance
   */
  videoId: PropTypes.string.isRequired,
  /**
   * Height to be passed for Video consumption
   */
  height: PropTypes.number.isRequired,
  /**
   * Width to be passed for Video consumption
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
  * When false this prop will force sister player to stop playing
  * */
  active: PropTypes.bool,
  /**
   * Callback for handling the video loading completion
   */
  onPlayerReady: PropTypes.func,
  /**
   * Callback for handling the play initialization of the video
   */
  onPlay: PropTypes.func,
  /**
   * Callback for handling the replay of each video
   */
  onReplay: PropTypes.func,
  /**
   * Callback for handling the completion of each video
   */
  onCompleted: PropTypes.func,
  /**
   * Callback for handling the milestone reached event of each video
   */
  onMilestones: PropTypes.func,
  /**
   * the src string is the video file to be played
   */
  src: PropTypes.string.isRequired,
  /**
   * className to be passed to the root node
   */
  className: PropTypes.string,
  /**
   * When true, videos will play when loaded
   */
  autostart: PropTypes.bool,
  /**
   * Array of video shapes for JWPlayer
   */
  playlist: PropTypes.arrayOf(PropTypes.shape({
    /**
     * file src of the video in the playlist
     */
    file: PropTypes.string,
    /**
     * title of the video in the playlist
     */
    title: PropTypes.string
  })),
  /**
   * The placeholder image for JWPlayer and SisterPlayer
   */
  image: PropTypes.string,
  /**
   * The provider string is the type of media to be played if passed
   */
  provider: PropTypes.string,
  /**
   * The used with JWPlayer flash videos to smooth buffering
   */
  smoothing: PropTypes.bool,
  /**
   * When true, the JW video will repeat
   */
  repeat: PropTypes.bool,
  /**
   * When true, the JW video will play muted
   */
  mute: PropTypes.bool,
  /**
   * When true, the JW control bar will remain docked
   */
  dock: PropTypes.bool,
  /**
   * String to pass type of stretching to JWPlayer
   */
  stretching: PropTypes.string,
  /**
   * When true, the control bar will be present for JWPlayer
   */
  controlbar: PropTypes.string,
  /**
   * custom configuration for all videos
   */
  config: PropTypes.object,
  /**
   * A placeholder component, passed to supported players
   */
  placeholder: PropTypes.object
};
export default Video;